/**
 * Site currency: Israeli Shekel (NIS / ILS). All prices are stored as numbers; display uses this helper.
 */
export const SITE_CURRENCY = {
  /** ISO 4217 (same unit as NIS) */
  code: "ILS" as const,
  /** Common English abbreviation */
  labelEn: "NIS",
  labelAr: "شيكل",
  symbol: "₪",
} as const;

/** Missing/empty UI token. Middot matches the rest of the site separators. */
export const EMPTY_DISPLAY = "·";

/** Format prices with Western numerals and English grouping (e.g. ₪120,000). */
export function formatSiteMoney(amount: number | string, locale: string = "en-US"): string {
  const n = Number(amount);
  if (Number.isNaN(n)) return EMPTY_DISPLAY;
  return new Intl.NumberFormat(locale, {
    numberingSystem: "latn",
    style: "currency",
    currency: SITE_CURRENCY.code,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
}

/** Counts, mileage, years. Latin digits while UI stays Arabic. */
export function formatSiteNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat("en-US", {
    numberingSystem: "latn",
    ...options,
  }).format(value);
}

/** Site dates/times in English (Western numerals). */
export function formatSiteDateTime(date: Date | string, locale = "en-US"): string {
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return EMPTY_DISPLAY;
  return d.toLocaleString(locale, {
    numberingSystem: "latn",
    dateStyle: "medium",
    timeStyle: "short",
  });
}

/** Short suffix for labels, e.g. form fields */
export const PRICE_FIELD_SUFFIX = `(${SITE_CURRENCY.labelAr} · ${SITE_CURRENCY.labelEn})`;
